import {v4 as uuidv4} from "uuid"
import {
	AchPayment,
	BookPayment,
	isAchPayment,
	isBookPayment,
	isPlaidVerificationMethod,
	isWirePayment,
	Payment,
	PaymentStatus,
	WirePayment,
} from "../resources/payment"

export const PAYMENT_LIMIT_EXCEEDED_REASONS = new Set([
	"DailyACHCreditLimitExceeded",
	"DailyACHDebitLimitExceeded",
	"MonthlyACHCreditLimitExceeded",
	"MonthlyACHDebitLimitExceeded",
	"DailyBookCreditLimitExceeded",
	"DailyBookDebitLimitExceeded",
	"MonthlyBookCreditLimitExceeded",
	"MonthlyBookDebitLimitExceeded",
	"CounterpartyDailyBookCreditLimitExceeded",
	"CounterpartyDailyBookDebitLimitExceeded",
	"CounterpartyMonthlyBookCreditLimitExceeded",
	"CounterpartyMonthlyBookDebitLimitExceeded",
	"DailyWireLimitExceeded",
	"MonthlyWireLimitExceeded",
])

export function isPaymentLimitExceededReason(reason?: string): boolean {
	return reason != null && PAYMENT_LIMIT_EXCEEDED_REASONS.has(reason)
}

export type RecreatePaymentResult = {ok: true; payload: Record<string, unknown>} | {ok: false; message: string}

export type RecreatePaymentPermission = {
	resource: "payment.achCredit" | "payment.achDebit" | "payment.book" | "payment.wire"
	action: "create"
}

/** Matches POST /payments RBAC (rail create), not payment.manage (operator-only). */
export function getRecreatePaymentPermission(payment: Payment): RecreatePaymentPermission | undefined {
	if (payment.attributes.status !== PaymentStatus.Rejected) {
		return undefined
	}
	if (!buildRecreatePaymentPayload(payment, payment.id).ok) {
		return undefined
	}
	if (isAchPayment(payment)) {
		return payment.attributes.direction === "Debit"
			? {resource: "payment.achDebit", action: "create"}
			: {resource: "payment.achCredit", action: "create"}
	}
	if (isBookPayment(payment)) {
		return {resource: "payment.book", action: "create"}
	}
	if (isWirePayment(payment)) {
		return {resource: "payment.wire", action: "create"}
	}
	return undefined
}

function optionalAchAttributes(payment: AchPayment): Record<string, unknown> {
	const attrs: Record<string, unknown> = {}
	if (payment.attributes.addenda) attrs.addenda = payment.attributes.addenda
	if (payment.attributes.sameDay) attrs.sameDay = payment.attributes.sameDay
	if (payment.attributes.secCode) attrs.secCode = payment.attributes.secCode
	if (payment.attributes.clearingDaysOverride != null) {
		attrs.clearingDaysOverride = payment.attributes.clearingDaysOverride
	}
	if (payment.attributes.tags) attrs.tags = payment.attributes.tags
	return attrs
}

export function buildRecreatePaymentPayload(payment: Payment, sourcePaymentId: string): RecreatePaymentResult {
	const idempotencyKey = `dashboard-recreate-from-${sourcePaymentId}-${uuidv4()}`

	if (payment.attributes.status !== PaymentStatus.Rejected) {
		return {ok: false, message: "Only rejected payments can be recreated."}
	}

	if (isAchPayment(payment)) {
		return buildAchRecreatePayload(payment, idempotencyKey)
	}

	if (isBookPayment(payment)) {
		return buildBookRecreatePayload(payment, idempotencyKey)
	}

	if (isWirePayment(payment)) {
		return buildWireRecreatePayload(payment, idempotencyKey)
	}

	return {ok: false, message: "This payment type cannot be recreated from the Dashboard."}
}

function buildAchRecreatePayload(payment: AchPayment, idempotencyKey: string): RecreatePaymentResult {
	if (isPlaidVerificationMethod(payment)) {
		return {
			ok: false,
			message:
				"Plaid-verified ACH payments cannot be recreated automatically. Create a new payment via the API with a fresh Plaid processor token.",
		}
	}

	const account = payment.relationships.account.data
	const baseAttributes = {
		amount: payment.attributes.amount,
		direction: payment.attributes.direction,
		description: payment.attributes.description,
		idempotencyKey,
		...optionalAchAttributes(payment),
	}

	if (payment.relationships.counterparty?.data) {
		return {
			ok: true,
			payload: {
				type: "achPayment",
				attributes: baseAttributes,
				relationships: {
					account: {data: account},
					counterparty: {data: payment.relationships.counterparty.data},
				},
			},
		}
	}

	const counterparty = payment.attributes.counterparty
	if (
		!counterparty?.name ||
		!counterparty?.routingNumber ||
		!counterparty?.accountNumber ||
		!counterparty?.accountType
	) {
		return {ok: false, message: "Missing counterparty details on the rejected payment."}
	}

	return {
		ok: true,
		payload: {
			type: "achPayment",
			attributes: {
				...baseAttributes,
				counterparty: {
					name: counterparty.name,
					routingNumber: counterparty.routingNumber,
					accountNumber: counterparty.accountNumber,
					accountType: counterparty.accountType,
				},
			},
			relationships: {
				account: {data: account},
			},
		},
	}
}

function buildBookRecreatePayload(payment: BookPayment, idempotencyKey: string): RecreatePaymentResult {
	const counterpartyAccount = payment.relationships.counterpartyAccount?.data
	if (!counterpartyAccount) {
		return {ok: false, message: "Missing counterparty account on the rejected book payment."}
	}

	const attributes: Record<string, unknown> = {
		amount: payment.attributes.amount,
		description: payment.attributes.description,
		idempotencyKey,
	}
	if (payment.attributes.direction) attributes.direction = payment.attributes.direction
	if (payment.attributes.tags) attributes.tags = payment.attributes.tags

	return {
		ok: true,
		payload: {
			type: "bookPayment",
			attributes,
			relationships: {
				account: {data: payment.relationships.account.data},
				counterpartyAccount: {data: counterpartyAccount},
			},
		},
	}
}

function buildWireRecreatePayload(payment: WirePayment, idempotencyKey: string): RecreatePaymentResult {
	const counterparty = payment.attributes.counterparty
	if (!counterparty?.name || !counterparty?.routingNumber || !counterparty?.accountNumber || !counterparty?.address) {
		return {ok: false, message: "Missing wire counterparty details on the rejected payment."}
	}

	const attributes: Record<string, unknown> = {
		amount: payment.attributes.amount,
		direction: "Credit",
		description: payment.attributes.description,
		counterparty: {
			name: counterparty.name,
			routingNumber: counterparty.routingNumber,
			accountNumber: counterparty.accountNumber,
			address: counterparty.address,
		},
		idempotencyKey,
	}
	if (payment.attributes.tags) attributes.tags = payment.attributes.tags

	return {
		ok: true,
		payload: {
			type: "wirePayment",
			attributes,
			relationships: {
				account: {data: payment.relationships.account.data},
			},
		},
	}
}

export function getAccountLimitsUrl(accountId: string): string {
	return `/accounts/${accountId}#accountTab=AccountLimits`
}
